/**
 * Utility functions for processing multi-draw promotions
 */

/**
 * Process electronic entries to handle multi-draw promotions
 * @param {Array} electronicEntriesSummary - Raw API response array
 * @param {Object} promoDrawsScheduleConfig - Configuration object
 * @param {Function} shouldPublish - Function to check if promotion should be published
 * @returns {Array} Processed promotions with combined draw data
 */
export const processMultiDrawPromotions = (electronicEntriesSummary, promoDrawsScheduleConfig, shouldPublish) => {
  const processedPromotions = [];
  const usedDrawIds = new Set();

  // Get published configs for reference
  const publishedConfigs = Object.entries(promoDrawsScheduleConfig)
    .filter(([_, config]) => shouldPublish(config))
    .reduce((acc, [promotionId, config]) => {
      acc[promotionId] = config;
      return acc;
    }, {});

  // Check each published config to see if it's multi-draw
  Object.entries(publishedConfigs).forEach(([mainPromotionId, config]) => {
    if (config.isMultiDraw && config.subDraws) {
      // Find all API responses that match this multi-draw promotion's subDraws
      const subDrawData = config.subDraws.map(subDrawConfig => {
        const apiDraw = electronicEntriesSummary.find(draw =>
          String(draw.promotion_id).toUpperCase() === String(subDrawConfig.promotionId).toUpperCase()
        );

        if (apiDraw) {
          usedDrawIds.add(apiDraw.promotion_id);
          return {
            ...apiDraw,
            labelKey: subDrawConfig.labelKey
          };
        }
        return null;
      }).filter(Boolean);

      if (subDrawData.length > 0) {
        // Create a combined draw object for the multi-draw promotion
        const combinedDraw = {
          promotion_id: mainPromotionId,
          promotion_name: config.key,
          total_tickets: subDrawData.reduce((sum, draw) => sum + (draw.total_tickets || 0), 0),
          // Use properties from first subdraw for other fields
          ...subDrawData[0]
        };

        processedPromotions.push({
          draw: combinedDraw,
          config,
          subDrawData
        });
      }
    } else {
      // Handle single-draw promotions (handle both string and number IDs)
      const apiDraw = electronicEntriesSummary.find(draw =>
        String(draw.promotion_id) === String(mainPromotionId)
      );

      if (apiDraw && !usedDrawIds.has(apiDraw.promotion_id)) {
        usedDrawIds.add(apiDraw.promotion_id);
        processedPromotions.push({
          draw: apiDraw,
          config,
          subDrawData: null
        });
      }
    }
  });

  return processedPromotions;
};

/**
 * Build promo data object with translations and config
 * @param {Object} config - Promotion configuration
 * @param {Function} t - Translation function
 * @returns {Object} Complete promo data object
 */
export const buildPromoData = (config, t) => {
  const friendlyKey = config?.key;

  let promoData = friendlyKey ? t(`promoDrawsPage.promos.${friendlyKey}`, {
    returnObjects: true,
  }) : {};

  // Check if translation returned an object or fallback string
  if (typeof promoData !== 'object' || promoData === null) {
    console.warn(`Missing translation for promo key: ${friendlyKey}`);
    promoData = {};
  }

  // Add timing and config fields to promoData
  return Object.assign(promoData, {
    earning_start: config?.earning_start,
    earning_end: config?.earning_end,
    draw_end: config?.draw_end,
    key: config?.key,
    isMultiDraw: config?.isMultiDraw,
    mergeTickets: config?.mergeTickets
  });
};
